import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCog, ChevronLeft, ChevronDown, ShoppingCart, Package, CreditCard,
  FileText, MapPin, Loader2, AlertCircle, Store, Users2, Search
} from 'lucide-react';
import { api } from '@/api/apiClient';
import EmptyState from '@/components/shared/EmptyState';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { cur } from '@/lib/currency';

/* ── helpers ──────────────────────────────────────────────────────────── */
const fmt0 = (n) => Number(n || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 });
const fmt2 = (n) => Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATE_LABEL = { draft: 'مسودة', sent: 'مرسل', sale: 'مؤكد', done: 'منتهي', cancel: 'ملغي' };
const STATE_COLOR = {
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  sale: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancel: 'bg-red-50 text-red-700 border-red-200',
};
const PAY_LABEL = { not_paid: 'غير مدفوعة', partial: 'جزئي', paid: 'مدفوعة', in_payment: 'قيد الدفع', reversed: 'معكوسة' };
const PAY_COLOR = {
  not_paid: 'bg-red-50 text-red-700 border-red-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_payment: 'bg-blue-50 text-blue-700 border-blue-200',
};

const PERIODS = [
  { key: 'today', label: 'اليوم' },
  { key: 'week', label: 'الأسبوع' },
  { key: 'month', label: 'الشهر' },
  { key: 'all', label: 'الكل' },
];

function periodRange(period) {
  // Cairo ≈ UTC+2/3; offset to keep "today" aligned with local day.
  const now = new Date(Date.now() + 2 * 3600 * 1000);
  const iso = (d) => d.toISOString().slice(0, 10);
  if (period === 'today') return { from: iso(now), to: iso(now) };
  if (period === 'week') {
    const s = new Date(now);
    const dow = (s.getUTCDay() + 6) % 7; // Monday = 0
    s.setUTCDate(s.getUTCDate() - dow);
    return { from: iso(s), to: iso(now) };
  }
  if (period === 'month') {
    const s = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { from: iso(s), to: iso(now) };
  }
  return { from: null, to: null }; // all
}

function unwrapList(res) {
  const items = res?.data || [];
  const total = res?.pagination?.total ?? res?.total ?? (Array.isArray(items) ? items.length : 0);
  return { items: Array.isArray(items) ? items : [], total };
}

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).trim() || '؟';
}

/* ── reps list ────────────────────────────────────────────────────────── */
function RepCard({ rep, onClick }) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.99 }}
      className="text-right w-full bg-card border border-border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">
          {initials(rep.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground truncate">{rep.name}</p>
          <p className="text-xs text-muted-foreground truncate">{rep.login}</p>
        </div>
        <ChevronLeft className="w-5 h-5 text-muted-foreground shrink-0" />
      </div>

      {rep.warehouses?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {rep.warehouses.slice(0, 3).map((w, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-muted text-muted-foreground rounded-full px-2 py-0.5">
              <Store className="w-3 h-3" /> {w}
            </span>
          ))}
          {rep.warehouses.length > 3 && (
            <span className="text-[11px] text-muted-foreground px-1">+{rep.warehouses.length - 3}</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 mt-3">
        <div className="rounded-xl bg-blue-50 border border-blue-100 px-2 py-2 text-center">
          <p className="text-base font-bold text-blue-700 leading-none">{fmt0(rep.open_orders)}</p>
          <p className="text-[10px] text-blue-700/70 mt-1">طلبات مفتوحة</p>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 px-2 py-2 text-center">
          <p className="text-base font-bold text-amber-700 leading-none">{fmt0(rep.due_invoices)}</p>
          <p className="text-[10px] text-amber-700/70 mt-1">فواتير مستحقة</p>
        </div>
        <div className="rounded-xl bg-red-50 border border-red-100 px-2 py-2 text-center">
          <p className="text-sm font-bold text-red-700 leading-none">{fmt0(rep.outstanding)}</p>
          <p className="text-[10px] text-red-700/70 mt-1">مديونية</p>
        </div>
      </div>
    </motion.button>
  );
}

/* ── collapsible section ──────────────────────────────────────────────── */
function Section({ icon: Icon, title, accent, open, onToggle, loading, summary, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 text-right">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground">{title}</p>
          {summary && <p className="text-xs text-muted-foreground mt-0.5">{summary}</p>}
        </div>
        {loading
          ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          : <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-border">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Row({ children }) {
  return <div className="flex items-center justify-between gap-2 py-2 border-b border-border/60 last:border-0">{children}</div>;
}
function Badge({ label, cls }) {
  return <span className={`text-[11px] rounded-full px-2 py-0.5 border ${cls}`}>{label}</span>;
}
function MiniEmpty({ text }) {
  return <p className="text-sm text-muted-foreground text-center py-4">{text}</p>;
}

/* ── rep detail ───────────────────────────────────────────────────────── */
function RepDetail({ rep, onBack }) {
  const [period, setPeriod] = useState('month');
  const [open, setOpen] = useState({ orders: true, products: false, colls: false, invoices: false, visits: false });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const [orders, setOrders] = useState(null);
  const [products, setProducts] = useState(null);
  const [colls, setColls] = useState(null);
  const [invoices, setInvoices] = useState({ all: null, due: null });
  const [invTab, setInvTab] = useState('all');
  const [visits, setVisits] = useState(null);
  const [busy, setBusy] = useState({});

  const setB = (k, v) => setBusy((b) => ({ ...b, [k]: v }));
  const range = periodRange(period);
  const dateParams = range.from ? { date_from: range.from, date_to: range.to } : {};

  const loadOrders = useCallback(async () => {
    setB('orders', true);
    try {
      const res = await api.get('/sellgo_so', { salesman_uid: rep.uid, limit: 100, ...dateParams });
      setOrders(unwrapList(res));
    } catch { setOrders({ items: [], total: 0, error: true }); }
    finally { setB('orders', false); }
  }, [rep.uid, period]); // eslint-disable-line

  const loadColls = useCallback(async () => {
    setB('colls', true);
    try {
      const res = await api.get('/sellgo_coll_history', { salesman_uid: rep.uid, limit: 100, ...dateParams });
      setColls(unwrapList(res));
    } catch { setColls({ items: [], total: 0, error: true }); }
    finally { setB('colls', false); }
  }, [rep.uid, period]); // eslint-disable-line

  const loadVisits = useCallback(async () => {
    setB('visits', true);
    try {
      const res = await api.get('/visits', { user_id: rep.uid, limit: 100, ...dateParams });
      setVisits(unwrapList(res));
    } catch { setVisits({ items: [], total: 0, error: true }); }
    finally { setB('visits', false); }
  }, [rep.uid, period]); // eslint-disable-line

  const loadProducts = useCallback(async () => {
    setB('products', true);
    try {
      const res = await api.get('/sellgo_inventory', { rep_uid: rep.uid, limit: 300 });
      setProducts(unwrapList(res));
    } catch { setProducts({ items: [], total: 0, error: true }); }
    finally { setB('products', false); }
  }, [rep.uid]);

  const loadInvoices = useCallback(async (status) => {
    setB('invoices', true);
    try {
      const res = await api.get('/sellgo_rep_invoices', { rep_uid: rep.uid, status, limit: 200 });
      setInvoices((s) => ({ ...s, [status]: unwrapList(res) }));
    } catch { setInvoices((s) => ({ ...s, [status]: { items: [], total: 0, error: true } })); }
    finally { setB('invoices', false); }
  }, [rep.uid]);

  // period-dependent
  useEffect(() => { loadOrders(); loadColls(); loadVisits(); }, [loadOrders, loadColls, loadVisits]);
  // one-time
  useEffect(() => { loadProducts(); loadInvoices('all'); loadInvoices('due'); }, [loadProducts, loadInvoices]);

  const ordersTotal = (orders?.items || []).reduce((s, o) => s + Number(o.amount_total || 0), 0);
  const collsTotal = (colls?.items || []).reduce((s, c) => s + Number(c.amount || 0), 0);
  const invList = invoices[invTab];

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="w-9 h-9 rounded-xl border border-border bg-card flex items-center justify-center hover:bg-muted shrink-0">
          <ChevronLeft className="w-5 h-5 rotate-180" />
        </button>
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">
          {initials(rep.name)}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-foreground truncate">{rep.name}</h2>
          <p className="text-xs text-muted-foreground truncate">
            {rep.warehouses?.length ? rep.warehouses.join('، ') : rep.login}
          </p>
        </div>
      </div>

      {/* period filter */}
      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              period === p.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* أوامر البيع */}
      <Section
        icon={ShoppingCart} title="أوامر البيع" accent="bg-blue-50 text-blue-600"
        open={open.orders} onToggle={() => toggle('orders')} loading={busy.orders}
        summary={orders ? `${fmt0(orders.total)} طلب • ${fmt2(ordersTotal)} ${cur('ج.م')}` : '—'}
      >
        {!orders ? <MiniEmpty text="جارٍ التحميل…" /> : orders.items.length === 0 ? <MiniEmpty text="لا توجد أوامر في هذه الفترة" /> : (
          <div>
            {orders.items.slice(0, 15).map((o) => (
              <Row key={o.id}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{o.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {Array.isArray(o.partner_id) ? o.partner_id[1] : ''} • {(o.date_order || '').slice(0, 10)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge label={STATE_LABEL[o.state] || o.state} cls={STATE_COLOR[o.state] || 'bg-muted text-muted-foreground border-border'} />
                  <span className="text-sm font-semibold text-foreground">{fmt2(o.amount_total)}</span>
                </div>
              </Row>
            ))}
            {orders.items.length > 15 && <p className="text-xs text-muted-foreground text-center pt-2">عرض أول 15 من {fmt0(orders.total)}</p>}
          </div>
        )}
      </Section>

      {/* منتجات المخزن */}
      <Section
        icon={Package} title="منتجات المخزن" accent="bg-emerald-50 text-emerald-600"
        open={open.products} onToggle={() => toggle('products')} loading={busy.products}
        summary={products ? `${fmt0(products.total)} صنف متوفر` : '—'}
      >
        {!products ? <MiniEmpty text="جارٍ التحميل…" /> : products.items.length === 0 ? <MiniEmpty text="لا توجد أصناف بالمخزن" /> : (
          <div>
            {products.items.slice(0, 20).map((p) => (
              <Row key={p.id}>
                <p className="text-sm font-medium text-foreground truncate min-w-0">{p.name || p.display_name}</p>
                <span className="text-sm font-semibold text-emerald-700 shrink-0">
                  {fmt0(p.qty_available)} {p.uom_name || ''}
                </span>
              </Row>
            ))}
            {products.items.length > 20 && <p className="text-xs text-muted-foreground text-center pt-2">عرض أول 20 من {fmt0(products.total)}</p>}
          </div>
        )}
      </Section>

      {/* التحصيلات */}
      <Section
        icon={CreditCard} title="التحصيلات" accent="bg-violet-50 text-violet-600"
        open={open.colls} onToggle={() => toggle('colls')} loading={busy.colls}
        summary={colls ? `${fmt0(colls.total)} عملية • ${fmt2(collsTotal)} ${cur('ج.م')}` : '—'}
      >
        {!colls ? <MiniEmpty text="جارٍ التحميل…" /> : colls.items.length === 0 ? <MiniEmpty text="لا توجد تحصيلات في هذه الفترة" /> : (
          <div>
            {colls.items.slice(0, 15).map((c) => (
              <Row key={c.id}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {Array.isArray(c.partner_id) ? c.partner_id[1] : (c.name || 'تحصيل')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {(c.date || '').slice(0, 10)}
                    {c.is_advance ? ' • دفعة مقدمة' : (c.invoices?.length ? ` • ${c.invoices.length} فاتورة` : '')}
                  </p>
                </div>
                <span className="text-sm font-semibold text-violet-700 shrink-0">{fmt2(c.amount)}</span>
              </Row>
            ))}
            {colls.items.length > 15 && <p className="text-xs text-muted-foreground text-center pt-2">عرض أول 15 من {fmt0(colls.total)}</p>}
          </div>
        )}
      </Section>

      {/* الفواتير */}
      <Section
        icon={FileText} title="الفواتير" accent="bg-amber-50 text-amber-600"
        open={open.invoices} onToggle={() => toggle('invoices')} loading={busy.invoices}
        summary={
          invoices.all || invoices.due
            ? `الكل ${fmt0(invoices.all?.total || 0)} • مستحقة ${fmt0(invoices.due?.total || 0)}`
            : '—'
        }
      >
        <div className="flex gap-2 mb-2">
          {[{ k: 'all', t: 'كل الفواتير' }, { k: 'due', t: 'المستحقة' }].map((tab) => (
            <button
              key={tab.k}
              onClick={() => setInvTab(tab.k)}
              className={`flex-1 px-3 py-1.5 rounded-xl text-sm border transition-colors ${
                invTab === tab.k ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {tab.t} {invoices[tab.k] ? `(${fmt0(invoices[tab.k].total)})` : ''}
            </button>
          ))}
        </div>
        {!invList ? <MiniEmpty text="جارٍ التحميل…" /> : invList.items.length === 0 ? <MiniEmpty text="لا توجد فواتير" /> : (
          <div>
            {invList.items.slice(0, 15).map((f) => (
              <Row key={f.id}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{f.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{f.partner} • {f.date}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge label={PAY_LABEL[f.payment_state] || f.payment_state} cls={PAY_COLOR[f.payment_state] || 'bg-muted text-muted-foreground border-border'} />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground leading-none">{fmt2(f.amount_total)}</p>
                    {Number(f.amount_residual) > 0 && (
                      <p className="text-[11px] text-red-600 mt-0.5">متبقي {fmt2(f.amount_residual)}</p>
                    )}
                  </div>
                </div>
              </Row>
            ))}
            {invList.items.length > 15 && <p className="text-xs text-muted-foreground text-center pt-2">عرض أول 15 من {fmt0(invList.total)}</p>}
          </div>
        )}
      </Section>

      {/* الزيارات */}
      <Section
        icon={MapPin} title="الزيارات" accent="bg-rose-50 text-rose-600"
        open={open.visits} onToggle={() => toggle('visits')} loading={busy.visits}
        summary={visits ? `${fmt0(visits.total)} زيارة` : '—'}
      >
        {!visits ? <MiniEmpty text="جارٍ التحميل…" /> : visits.items.length === 0 ? <MiniEmpty text="لا توجد زيارات في هذه الفترة" /> : (
          <div>
            {visits.items.slice(0, 15).map((v) => (
              <Row key={v.id}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {v.partner_name || (Array.isArray(v.partner_id) ? v.partner_id[1] : 'زيارة')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{(v.visit_date || '').slice(0, 16).replace('T', ' ')}</p>
                </div>
                <Badge
                  label={v.state === 'done' ? 'مكتملة' : v.state === 'in_progress' ? 'جارية' : v.state === 'cancel' ? 'ملغية' : 'مخططة'}
                  cls={v.state === 'done' ? STATE_COLOR.done : v.state === 'cancel' ? STATE_COLOR.cancel : 'bg-blue-50 text-blue-700 border-blue-200'}
                />
              </Row>
            ))}
            {visits.items.length > 15 && <p className="text-xs text-muted-foreground text-center pt-2">عرض أول 15 من {fmt0(visits.total)}</p>}
          </div>
        )}
      </Section>
    </div>
  );
}

/* ── page ─────────────────────────────────────────────────────────────── */
export default function RepMonitoring() {
  const { odooSession } = useOdooAuth();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.get('/sellgo_team_reps');
        if (alive) setReps(res?.data || []);
      } catch (e) {
        if (alive) setError(e?.message || 'تعذّر تحميل المناديب');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = reps.filter((r) =>
    !q.trim() || (r.name || '').includes(q) || (r.login || '').includes(q)
  );

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      {!selected && (
        <>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <UserCog className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">متابعة المناديب</h1>
              <p className="text-sm text-muted-foreground">
                {odooSession?.role === 'manager' ? 'كل المناديب' : 'المناديب التابعين لك'}
                {reps.length ? ` • ${fmt0(reps.length)}` : ''}
              </p>
            </div>
          </div>

          {reps.length > 4 && (
            <div className="relative">
              <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ابحث باسم المندوب…"
                className="w-full bg-card border border-border rounded-xl pr-9 pl-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : error ? (
            <EmptyState icon={AlertCircle} title="حدث خطأ" description={error} />
          ) : filtered.length === 0 ? (
            <EmptyState icon={Users2} title="لا يوجد مناديب" description="لم يتم العثور على مناديب لعرضهم." />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {filtered.map((rep) => (
                <RepCard key={rep.uid} rep={rep} onClick={() => setSelected(rep)} />
              ))}
            </div>
          )}
        </>
      )}

      {selected && <RepDetail rep={selected} onBack={() => setSelected(null)} />}
    </div>
  );
}
